import React from 'react';

import { Input } from '@/components/form/Input';
import { Container } from '@/components/ui';
import { cn } from '@/utils/classname';

export interface NegotiatedPriceInputProps {
  value: number;
  onChange: (value: number) => void;
  msrp?: number;
  usagePrice?: number;
  disabled?: boolean;
  className?: string;
  title?: string;
}

const inputClassName =
  'border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary w-50 rounded-md border-2 px-4 py-2 transition-all focus:ring-2 focus:ring-offset-1 focus:outline-none';

/**
 * NegotiatedPriceInput - Reusable component for negotiated price input with discount calculation
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export const NegotiatedPriceInput: React.FC<NegotiatedPriceInputProps> = ({
  value,
  onChange,
  msrp,
  usagePrice,
  disabled = false,
  className,
  title = 'Negotiated Price',
}) => {
  // Calculate MSRP discount percentage
  const renderMSRPDiscount = () => {
    if (!msrp || msrp <= 0 || value <= 0) return null;

    const msrpDiscountPercent = ((msrp - value) / msrp) * 100;
    const isDiscount = msrpDiscountPercent > 0;
    const isPremium = msrpDiscountPercent < 0;
    const isHighDiscount = isDiscount && msrpDiscountPercent > 10;
    const isHighPremium = isPremium && msrpDiscountPercent < -10;

    return (
      <div className="text-xs">
        <span className="text-chariot-text-secondary">
          Discount from MSRP:{' '}
        </span>
        <span
          className={`${
            isHighDiscount
              ? 'font-bold text-red-600'
              : isHighPremium
                ? 'font-bold text-green-500'
                : isPremium
                  ? 'text-green-500'
                  : 'text-chariot-status-warning'
          }`}
        >
          {isDiscount
            ? `${Math.abs(msrpDiscountPercent).toFixed(1)}%`
            : `-${Math.abs(msrpDiscountPercent).toFixed(1)}%`}
        </span>
      </div>
    );
  };

  // Calculate Usage discount percentage
  const renderUsageDiscount = () => {
    if (!usagePrice || usagePrice <= 0 || value <= 0) return null;

    const usageDiscountPercent = ((usagePrice - value) / usagePrice) * 100;
    const isDiscount = usageDiscountPercent > 0;
    const isPremium = usageDiscountPercent < 0;
    const isHighDiscount = isDiscount && usageDiscountPercent > 10;
    const isHighPremium = isPremium && usageDiscountPercent < -10;

    return (
      <div className="text-xs">
        <span className="text-chariot-text-secondary">
          Discount from Usage:{' '}
        </span>
        <span
          className={`${
            isHighDiscount
              ? 'font-bold text-red-600'
              : isHighPremium
                ? 'font-bold text-green-500'
                : isPremium
                  ? 'text-green-500'
                  : 'text-chariot-status-warning'
          }`}
        >
          {isDiscount
            ? `${Math.abs(usageDiscountPercent).toFixed(1)}%`
            : `-${Math.abs(usageDiscountPercent).toFixed(1)}%`}
        </span>
      </div>
    );
  };

  return (
    <section className={cn('space-y-4', className)}>
      <h3 className="text-chariot-text-primary text-lg font-semibold">
        {title}
      </h3>
      <Container isSelected className="bg-chariot-background-primary">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-chariot-text-primary text-sm font-medium">
              Negotiated Annual Price
            </h2>
            <div className="mt-2 space-y-1">
              {renderMSRPDiscount()}
              {renderUsageDiscount()}
            </div>
          </div>
          <div className="text-chariot-brand-primary text-2xl font-bold">
            <Input
              name="negotiatedPrice"
              className={cn(inputClassName, 'text-right')}
              value={value.toString()}
              onChange={e =>
                !disabled && onChange(parseInt(e.target.value) || 0)
              }
              placeholder="Enter price"
              disabled={disabled}
            />
          </div>
        </div>
      </Container>
    </section>
  );
};
